import React, { useState } from 'react';
import {
  Button,
  CircularProgress,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
// newer & better version of Grid
import Grid2 from '@mui/material/Unstable_Grid2';
import { styled } from '@mui/material/styles';
import { reportError } from 'actions/errorActions';
import Tip from 'components/Tip';
import config from 'config';
import { useDispatch, useSelector } from 'react-redux';
import apiService from 'services/apiService';
import texts from 'texts';
import {
  translateKeyword,
  translation,
} from 'utils/translate';
import './Hakuvahti.css';
import {
  useParams,
  useSearchParams,
} from 'react-router-dom';

const operations = [
  'basic',
  /*'lausunto',*/
  'confirm',
  'cancel',
];
const StyledButton = styled(Button)({
  backgroundColor: 'var(--primary)',
  borderRadius: 5,
  border: 0,
  height: '48px',
  textTransform: 'none',
  color: 'white',
  fontSize: '16px',
  lineHeight: '18px',
  '&:hover': {
    backgroundColor: 'var(--primary-dark)',
  },
});

const WhiteButton = styled(Button)({
  backgroundColor: 'white',
  borderRadius: 5,
  border: '3px solid var(--primary)',
  height: '48px',
  marginLeft: 10,
  textTransform: 'none',
  color: 'var(--primary)',
  fontSize: '14px',
  lineHeight: '18px',
  '&:hover': {
    backgroundColor: 'var(--blue-verylight)',
  },
});

const WarningBox = styled(Button)({
  height: 56,
  backgroundColor: '#ffed65',
  textTransform: 'none',
  color: '#988600',
  fontSize: '14px',
  lineHeight: '18px',
  '&:hover': {
    backgroundColor: '#ffed65',
  },
});

const SuccessBox = styled(Button)({
  height: 56,
  backgroundColor: '#ace7ca',
  textTransform: 'none',
  color: '#009c50',
  fontSize: '14px',
  lineHeight: '18px',
  '&:hover': {
    backgroundColor: '#ace7ca',
  },
});

const Hakuvahti = () => {
  // load url parameters and use them to initiate state
  const [searchParams, setSearchParams] = useSearchParams({
    action: 'basic',
    email: '',
    code: '',
  });
  const [content, setContent] = useState({
    op: searchParams.get('action'),
    term: '',
    docType: 'all',
    mail: searchParams.get('email'),
    code: searchParams.get('code'),
  });

  const [status, setStatus] = useState('');
  const [disableButtons, setDisableButtons] =
    useState(false);

  const dispatch = useDispatch();
  const lang = useSelector((store) => store.lang);

  const changeOp = async (event) => {
    setStatus('');
    // clear url params, keep state
    setSearchParams();
    setContent({ ...content, op: event });
  };

  const resetForm = () => {
    // clear url params, reset state
    setSearchParams();
    setContent({
      ...content,
      term: '',
      docType: 'all',
      mail: '',
      code: '',
    });
  };

  const isContentValid = (content) => {
    // if operation is confirm or cancel, code must be present
    if (
      content.op === 'confirm' ||
      content.op === 'cancel'
    ) {
      if (content.code === '') return false;
    }

    // term cannot be empty when creating a subscription
    if (content.op === 'basic') {
      if (content.term === '') return false;
    }

    // return true if content.mail matches the email regex xx@xx.xx
    const emailRe = /\S+@\S+\.\S+/;
    return emailRe.test(content.mail);
  };

  const handleRequest = async () => {
    setDisableButtons(true);
    setStatus('processing');

    // check for problems with essential fields
    if (!isContentValid(content)) {
      setStatus('warning');
      setDisableButtons(false);
      return;
    }

    try {
      const response =
        await apiService.sendHakuvahtiRequest({
          ...content,
          lang: lang,
        });
      if (
        response.status === 200 ||
        response.status === 201
      ) {
        setStatus('success');
        resetForm();
      } else {
        setStatus('warning');
      }
    } catch (err) {
      dispatch(reportError(err));
    } finally {
      setDisableButtons(false);
    }
  };

  const bannerUrl =
    lang === 'se'
      ? '/hakuvahti_banner_swedish.png'
      : '/hakuvahti_banner.png';

  return (
    <>
      <img
        alt="banneri, Älä anna tiedon mennä ohi. Hakuvahti on hereillä puolestasi."
        src={bannerUrl}
      />
      <Grid2
        container
        direction={'column'}
        sx={{
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Grid2 container className="op-type-select-area">
          {operations.map((key) => (
            <Tip
              title={translation(
                'hakuvahti',
                key,
                'tooltip',
                lang,
              )}
              key={key}
            >
              <button
                type="button"
                className={
                  content.op === key
                    ? 'op-type-select op-type-select--active'
                    : 'op-type-select'
                }
                onClick={() => changeOp(key)}
              >
                {translation(
                  'hakuvahti',
                  key,
                  'name',
                  lang,
                )}
              </button>
            </Tip>
          ))}
        </Grid2>

        {/* forms */}
        <Grid2
          container
          direction={'column'}
          gap={1}
          xs={10}
          md={8}
          lg={6}
          sx={{
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {content.op === 'basic' ? (
            <BasicCreationForm
              lang={lang}
              content={content}
              setContent={setContent}
            />
          ) : content.op === 'confirm' ? (
            <ConfirmForm
              lang={lang}
              content={content}
              setContent={setContent}
            />
          ) : content.op === 'cancel' ? (
            <CancelForm
              lang={lang}
              content={content}
              setContent={setContent}
            />
          ) : (
            <div>
              Hakuvahti poissa käytöstä hetkellisesti.
              Pahoittelut häiriöstä.
            </div>
          )}

          <div
            style={{
              alignSelf: content.op === 'basic' && 'start',
            }}
          >
            <StyledButton
              disabled={disableButtons}
              onClick={handleRequest}
              variant="contained"
            >
              {translation(
                'hakuvahti',
                content.op,
                'request_button',
                lang,
              )}
            </StyledButton>
            <WhiteButton
              disabled={disableButtons}
              onClick={resetForm}
              variant="contained"
            >
              {translation(
                'hakuvahti',
                'emptyformbutton',
                lang,
              )}
            </WhiteButton>
          </div>
          <StatusBox
            status={status}
            setStatus={setStatus}
            operation={content.op}
          />
        </Grid2>

        <InfoSection lang={lang} />
      </Grid2>
    </>
  );
};

const BasicCreationForm = ({
  lang,
  content,
  setContent,
}) => {
  return (
    <Grid2 container direction={'column'} xs={12}>
      {/* HAKUSANA */}
      <h4 className="proximity-doctype-h4">
        {translation('hakuvahti', 'filter_info', lang)}
        <Tip
          title={translation('texts_tooltip_basic', lang)}
        >
          <span
            alt={texts.info.help}
            className="tooltip-help tippable"
          ></span>
        </Tip>
      </h4>
      <TextField
        className="basic-search-term"
        onChange={(e) =>
          setContent({
            ...content,
            term: e.target.value,
          })
        }
        key={'rajaa'}
        label={translation(
          'hakuvahti',
          'filter_labels',
          content.docType,
          lang,
        )}
        variant="outlined"
        value={content.term ? content.term : ''}
      />
      {/* ASIAKIRJATYYPPI */}
      <h4 className="proximity-doctype-h4">
        {translateKeyword('type', lang)}
        <Tip
          title={translation(
            'asiakirjatyyppi_tooltip',
            lang,
          )}
        >
          <span
            alt={translation(
              'asiakirjatyyppi_tooltip',
              lang,
            )}
            className="tooltip-help tippable"
          ></span>
        </Tip>
      </h4>
      <Select
        className="doctype-select"
        value={content.docType || 'all'}
        onChange={(e) =>
          setContent({
            ...content,
            docType: e.target.value,
          })
        }
      >
        {Object.entries(config.docGroups).map(
          ([key, value]) =>
            value.searchable && (
              <MenuItem value={key} key={key}>
                {translation(value.name, lang)}
              </MenuItem>
            ),
        )}
      </Select>
      {/* SÄHKÖPOSTI */}
      <h4 className="proximity-doctype-h4">
        {translation('hakuvahti', 'email', lang)}
      </h4>
      <TextField
        className="basic-text-field"
        onChange={(e) =>
          setContent({
            ...content,
            mail: e.target.value,
          })
        }
        label={translation('hakuvahti', 'email', lang)}
        variant="outlined"
        value={content.mail ? content.mail : ''}
      />
    </Grid2>
  );
};

const ConfirmForm = ({ lang, content, setContent }) => {
  return (
    <Grid2 container direction={'column'} xs={6}>
      <h4 className="proximity-doctype-h4">
        {translation('hakuvahti', 'email', lang)}
      </h4>
      <TextField
        className="basic-text-field"
        onChange={(e) =>
          setContent({
            ...content,
            mail: e.target.value,
          })
        }
        label={translation('hakuvahti', 'email', lang)}
        variant="outlined"
        value={content.mail ? content.mail : ''}
      />
      <h4 className="proximity-doctype-h4">
        {translation('hakuvahti', 'code', 'name', lang)}
        <Tip
          title={translation(
            'hakuvahti',
            'code',
            'tooltip',
            lang,
          )}
        >
          <span
            alt={translation(
              'hakuvahti',
              'code',
              'tooltip',
              lang,
            )}
            className="tooltip-help tippable"
          ></span>
        </Tip>
      </h4>
      <TextField
        className="basic-text-field"
        onChange={(e) =>
          setContent({
            ...content,
            code: e.target.value,
          })
        }
        label={translation(
          'hakuvahti',
          'code',
          'name',
          lang,
        )}
        variant="outlined"
        value={content.code ? content.code : ''}
      />
    </Grid2>
  );
};

const CancelForm = ({ lang, content, setContent }) => {
  return (
    <Grid2 container direction={'column'} xs={6}>
      <h4 className="proximity-doctype-h4">
        {translation('hakuvahti', 'email', lang)}
      </h4>
      <TextField
        className="basic-text-field"
        onChange={(e) =>
          setContent({
            ...content,
            mail: e.target.value,
          })
        }
        label={translation('hakuvahti', 'email', lang)}
        variant="outlined"
        value={content.mail ? content.mail : ''}
      />
      <h4 className="proximity-doctype-h4">
        {translation('hakuvahti', 'code', 'name', lang)}
        <Tip
          title={translation(
            'hakuvahti',
            'code',
            'tooltip',
            lang,
          )}
        >
          <span
            alt={translation(
              'hakuvahti',
              'code',
              'tooltip',
              lang,
            )}
            className="tooltip-help tippable"
          ></span>
        </Tip>
      </h4>
      <TextField
        className="basic-text-field"
        onChange={(e) =>
          setContent({
            ...content,
            code: e.target.value,
          })
        }
        label={translation(
          'hakuvahti',
          'code',
          'name',
          lang,
        )}
        variant="outlined"
        value={content.code ? content.code : ''}
      />
    </Grid2>
  );
};

const InfoSection = ({ lang }) => {
  return (
    <Grid2 xs={6} className="info-container">
      <h3 className="info-title">
        {translation('hakuvahti', 'about_title_1', lang)}
      </h3>
      <p>{translation('hakuvahti', 'about_1', lang)}</p>
      <p>{translation('hakuvahti', 'about_2', lang)}</p>

      <h3 className="info-title">
        {translation('hakuvahti', 'about_title_2', lang)}
      </h3>
      <ol>
        {translation(
          'hakuvahti',
          'instructions_list',
          lang,
        )?.map((step, i) => (
          <li key={i}>{step}</li>
        ))}
      </ol>

      <h3 className="info-title">
        {translation('hakuvahti', 'about_title_3', lang)}
      </h3>
      <p>{translation('hakuvahti', 'about_3', lang)}</p>
      <p>{translation('hakuvahti', 'about_4', lang)}</p>
    </Grid2>
  );
};

const StatusBox = ({ status, setStatus, operation }) => {
  const lang = useSelector((store) => store.lang);

  if (status === 'processing') return <CircularProgress />;

  if (status === 'warning')
    return (
      <WarningBox
        disableTouchRipple
        disableElevation
        onClick={
          status !== 'processing'
            ? () => setStatus('')
            : undefined
        }
        variant="contained"
        style={{
          alignSelf: operation === 'basic' && 'start',
        }}
      >
        {translation('hakuvahti', 'warning_notif', lang)}
      </WarningBox>
    );

  if (status === 'success')
    return (
      <SuccessBox
        disableTouchRipple
        disableElevation
        onClick={
          status !== 'processing'
            ? () => setStatus('')
            : undefined
        }
        variant="contained"
        style={{
          alignSelf: operation === 'basic' && 'start',
        }}
      >
        {translation('hakuvahti', 'success_notif', lang)}
      </SuccessBox>
    );

  return <></>;
};

export default Hakuvahti;
